/**
 * `MetricsView` — the admin's cross-account metrics dashboard
 * (ADR-0140 P1 metrics slice).
 *
 * Layout:
 *   1. Page header + date-range presets (24h / 7d / 30d / 90d / 1y)
 *   2. KPI summary tiles (always visible, pinned above the filter)
 *   3. Category filter chips (All / Growth / Engagement / Retention &
 *      churn / Revenue / Governance / Geography). Single-select.
 *      Persisted to localStorage as `sill.admin.metrics.category`.
 *   4. Sections — rendered only when their category is in scope of
 *      the current filter (All shows everything).
 *
 * Categories and their content:
 *   - Growth      → new accounts + active accounts time series
 *   - Engagement  → activity volume (discovery records) +
 *                   engagement intensity / tenure stat cards
 *   - Retention   → cohort heatmap (incl. median tenure column) +
 *                   churn + retention rates + churn-risk + cohort
 *                   breakdown (churned/reactivated/retained/dormant)
 *   - Revenue     → transactions succeeded + volume by currency
 *   - Governance  → mandates evaluated/approved + conversion strip
 *                   (approval rate, escalation resolution)
 *   - Geography   → top countries by records
 *
 * Two endpoints drive the view:
 *   - `getMetricsOverview(range)` — re-fetched on every range change.
 *   - `getMetricsCohorts(country?)` — fetched on mount and whenever
 *     the country filter changes. The cohort grid is range-independent
 *     (ALWAYS lifetime cohorts); only the country segment slices it.
 *
 * Each section owns its own loading / error / empty rendering so
 * partial failures don't blank the whole view (a flaky cohort fetch
 * still lets the snapshot + charts render).
 *
 * Voice: no marketing copy, no emoji. The dashboard is screen-shared
 * with investors; every number comes from the API, every "—" means
 * "no data, not zero".
 */

import { useCallback, useEffect, useState } from 'react';

import type {
  AdminMetricsAssistants,
  AdminMetricsCohorts,
  AdminMetricsOverview,
  ReportingRange,
} from '@sill/types';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import type { SetView } from '../../app/view-router.js';
import { formatIsoCompact } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';
import { AssistantsSection } from './sections/AssistantsSection.js';
import type { AssistantsSectionState } from './sections/AssistantsSection.js';
import { ChurnSection } from './sections/ChurnSection.js';
import type { ChurnSectionState } from './sections/ChurnSection.js';
import { CohortSection } from './sections/CohortSection.js';
import type { CohortSectionState } from './sections/CohortSection.js';
import { ConversionStrip } from './sections/ConversionStrip.js';
import type { ConversionStripState } from './sections/ConversionStrip.js';
import { EngagementSection } from './sections/EngagementSection.js';
import type { EngagementSectionState } from './sections/EngagementSection.js';
import { GeographySection } from './sections/GeographySection.js';
import type { GeographySectionState } from './sections/GeographySection.js';
import { KpiTilesSection } from './sections/KpiTilesSection.js';
import type { KpiTilesState } from './sections/KpiTilesSection.js';
import { TimeSeriesSection } from './sections/TimeSeriesSection.js';
import type { TimeSeriesState } from './sections/TimeSeriesSection.js';
import {
  CategoryFilter,
  isMetricsCategory,
} from './CategoryFilter.js';
import type { MetricsCategory } from './CategoryFilter.js';
import type { CountryFilterValue } from './CountryFilter.js';
import { RangePresets } from './RangePresets.js';

const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";

const RANGE_STORAGE_KEY = 'sill.admin.metrics.range';
const CATEGORY_STORAGE_KEY = 'sill.admin.metrics.category';

const VALID_RANGES: ReadonlySet<ReportingRange> = new Set([
  '24h',
  '7d',
  '30d',
  '90d',
  '1y',
]);

function readStoredRange(): ReportingRange {
  if (typeof window === 'undefined') return '30d';
  try {
    const raw = window.localStorage.getItem(RANGE_STORAGE_KEY);
    if (raw !== null && VALID_RANGES.has(raw as ReportingRange)) {
      return raw as ReportingRange;
    }
  } catch {
    /* storage disabled */
  }
  return '30d';
}

function writeStoredRange(range: ReportingRange): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(RANGE_STORAGE_KEY, range);
  } catch {
    /* storage disabled */
  }
}

function readStoredCategory(): MetricsCategory {
  if (typeof window === 'undefined') return 'all';
  try {
    const raw = window.localStorage.getItem(CATEGORY_STORAGE_KEY);
    if (isMetricsCategory(raw)) return raw;
  } catch {
    /* storage disabled */
  }
  return 'all';
}

function writeStoredCategory(cat: MetricsCategory): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CATEGORY_STORAGE_KEY, cat);
  } catch {
    /* storage disabled */
  }
}

type OverviewState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; overview: AdminMetricsOverview };

type CohortsState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; cohorts: AdminMetricsCohorts };

type AssistantsState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; assistants: AdminMetricsAssistants };

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.server_reason !== undefined && err.server_reason.length > 0) {
      return err.server_reason;
    }
    return err.kind;
  }
  return 'Unexpected error';
}

export interface MetricsViewProps {
  /** Threaded through to the ChurnSection so the churn-risk drill-down
   *  can navigate to an account-detail page via the existing
   *  view-router (same path AccountsListView uses). */
  setView: SetView;
}

export function MetricsView({ setView }: MetricsViewProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();

  const [range, setRange] = useState<ReportingRange>(() => readStoredRange());
  const [category, setCategory] = useState<MetricsCategory>(() =>
    readStoredCategory(),
  );
  const [overview, setOverview] = useState<OverviewState>({ kind: 'loading' });
  const [cohorts, setCohorts] = useState<CohortsState>({ kind: 'loading' });
  const [assistants, setAssistants] = useState<AssistantsState>({ kind: 'loading' });
  // Country segment for the cohort grid. `'all'` (the back-compat
  // default) renders the ungrouped lifetime grid; any other value is
  // an ISO-3166-1 alpha-2 OR the literal `'ZZ'` sentinel.
  const [cohortCountry, setCohortCountry] = useState<CountryFilterValue>('all');

  // Re-fetch overview on every range change. Cancellation token to
  // protect against late responses on rapid range toggles.
  useEffect(() => {
    let cancelled = false;
    setOverview({ kind: 'loading' });
    writeStoredRange(range);
    (async (): Promise<void> => {
      try {
        const res = await api.getMetricsOverview(range);
        if (cancelled) return;
        setOverview({ kind: 'ready', overview: res });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.kind === 'unauthorized') return;
        setOverview({ kind: 'error', message: errorMessage(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, range]);

  // Cohorts fetch — runs on mount AND on country-filter change. The
  // lifetime cohort grid is range-independent; only the country
  // segment slices it. Cancellation token guards against late
  // responses when the staff member flips segments rapidly.
  // ADR-0262 — assistant analytics follow the same range; own state so
  // a failure here never blanks the rest of the page.
  useEffect(() => {
    let cancelled = false;
    setAssistants({ kind: 'loading' });
    (async (): Promise<void> => {
      try {
        const res = await api.getMetricsAssistants(range);
        if (cancelled) return;
        setAssistants({ kind: 'ready', assistants: res });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.kind === 'unauthorized') return;
        setAssistants({ kind: 'error', message: errorMessage(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, range]);

  useEffect(() => {
    let cancelled = false;
    setCohorts({ kind: 'loading' });
    (async (): Promise<void> => {
      try {
        const res = await api.getMetricsCohorts(cohortCountry);
        if (cancelled) return;
        setCohorts({ kind: 'ready', cohorts: res });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.kind === 'unauthorized') return;
        setCohorts({ kind: 'error', message: errorMessage(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, cohortCountry]);

  const handleRangeChange = useCallback((next: ReportingRange) => {
    setRange(next);
  }, []);

  const handleCategoryChange = useCallback((next: MetricsCategory) => {
    // Filter in place — no scroll. The category chip lives near the top
    // of the page; jumping the scroll position was disorienting when
    // staff were mid-read in a long category panel.
    setCategory(next);
    writeStoredCategory(next);
  }, []);

  const handleCohortCountryChange = useCallback(
    (next: CountryFilterValue) => {
      setCohortCountry(next);
    },
    [],
  );

  // Per-section state projection — each section gets the slim type
  // it needs, not the whole `OverviewState`.
  const kpiState: KpiTilesState =
    overview.kind === 'ready'
      ? { kind: 'ready', snapshot: overview.overview.snapshot }
      : overview;
  const timeSeriesState: TimeSeriesState =
    overview.kind === 'ready'
      ? { kind: 'ready', overview: overview.overview }
      : overview;
  const conversionState: ConversionStripState =
    overview.kind === 'ready'
      ? { kind: 'ready', snapshot: overview.overview.snapshot }
      : overview;
  const cohortState: CohortSectionState = cohorts;
  const geographyState: GeographySectionState =
    overview.kind === 'ready'
      ? { kind: 'ready', geography: overview.overview.geography }
      : overview;
  const churnState: ChurnSectionState =
    overview.kind === 'ready'
      ? { kind: 'ready', churn: overview.overview.churn }
      : overview;
  const engagementState: EngagementSectionState =
    overview.kind === 'ready'
      ? { kind: 'ready', engagement: overview.overview.engagement }
      : overview;
  // Country options for the cohort filter — drawn from the overview's
  // top-N geography array. Empty until the overview is ready (the
  // filter still renders, just with "All countries" as the only option).
  const cohortCountryOptions =
    overview.kind === 'ready' ? overview.overview.geography : [];
  const assistantsState: AssistantsSectionState = assistants;

  // Window / generated_at — only meaningful when ready.
  const generatedAt =
    overview.kind === 'ready' ? overview.overview.generated_at : null;
  const windowFrom =
    overview.kind === 'ready' ? overview.overview.window.from : null;
  const windowTo =
    overview.kind === 'ready' ? overview.overview.window.to : null;

  const show = (cat: MetricsCategory): boolean =>
    category === 'all' || category === cat;

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-8 py-6 sm:py-10">
      {/* Page header */}
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 10,
        }}
      >
        Cross-account metrics
      </div>
      <div className="flex flex-wrap items-end justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
        <div className="min-w-0">
          <h1
            style={{
              fontFamily: serifFont,
              fontSize: 'clamp(28px, 6vw, 44px)',
              lineHeight: 1,
              color: C.text,
              letterSpacing: -1.5,
              margin: 0,
            }}
          >
            Metrics
          </h1>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.muted,
              letterSpacing: 0.5,
              marginTop: 10,
              lineHeight: 1.4,
              wordBreak: 'break-word',
            }}
          >
            {windowFrom !== null && windowTo !== null ? (
              <>
                Window: {formatIsoCompact(windowFrom)} → {formatIsoCompact(windowTo)}
              </>
            ) : (
              <>Resolving window…</>
            )}
            {generatedAt !== null ? (
              <>
                {' · '}
                <span style={{ color: C.dim }}>
                  as of {formatIsoCompact(generatedAt)}
                </span>
              </>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 2,
              textTransform: 'uppercase',
            }}
          >
            Range
          </span>
          <RangePresets
            value={range}
            onChange={handleRangeChange}
            disabled={overview.kind === 'loading'}
          />
        </div>
      </div>

      {/* KPI tiles — pinned ABOVE the category filter, always visible.
          The summary stays anchored across category switches so the
          staff always has the headline figures in view. */}
      <div className="mb-6 sm:mb-8">
        <KpiTilesSection state={kpiState} />
      </div>

      {/* Category filter bar — sticky-ish above the per-category
          sections. Plain (non-sticky) so it doesn't fight the page
          scroll on long category panels. */}
      <div
        className="mb-6 sm:mb-8 flex flex-col gap-2"
        style={{
          paddingTop: 4,
          paddingBottom: 4,
        }}
      >
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
          }}
        >
          Category
        </span>
        <CategoryFilter
          value={category}
          onChange={handleCategoryChange}
          disabled={overview.kind === 'loading'}
        />
      </div>

      {/* Sections — gated by category */}
      <div className="flex flex-col" style={{ gap: 36 }}>
        {show('growth') ? (
          <TimeSeriesSection
            state={timeSeriesState}
            kicker="Growth"
            title="Account growth"
            caption="New signups and the distinct accounts with real traffic in each daily bucket."
            charts={['growth_new_accounts', 'growth_active_accounts']}
          />
        ) : null}

        {show('engagement') ? (
          <>
            <TimeSeriesSection
              state={timeSeriesState}
              kicker="Engagement"
              title="Activity volume"
              caption="Real audit_record rows per day — the trustworthy volume signal."
              charts={['engagement_records']}
            />
            <EngagementSection state={engagementState} />
          </>
        ) : null}

        {show('retention') ? (
          <>
            <ChurnSection state={churnState} setView={setView} />
            <CohortSection
              state={cohortState}
              countries={cohortCountryOptions}
              country={cohortCountry}
              onCountryChange={handleCohortCountryChange}
            />
          </>
        ) : null}

        {show('revenue') ? (
          <TimeSeriesSection
            state={timeSeriesState}
            kicker="Revenue"
            title="Transactions & volume"
            caption="Live-mode settlements only — test and dry-run excluded."
            charts={['revenue_transactions', 'revenue_volume']}
          />
        ) : null}

        {show('governance') ? (
          <>
            <TimeSeriesSection
              state={timeSeriesState}
              kicker="Governance"
              title="Mandate evaluation"
              caption="Evaluated vs approved per day — the daily approval rate is the gap between the two lines."
              charts={['governance_mandates']}
            />
            <ConversionStrip state={conversionState} />
          </>
        ) : null}

        {show('geography') ? <GeographySection state={geographyState} /> : null}

        {show('assistants') ? <AssistantsSection state={assistantsState} /> : null}
      </div>
    </div>
  );
}
